import type { ChatbotStorageAdapter, ChatbotSnapshot } from '@footsteps/shared';
import type { WozCaseViewModel } from '../../woz-case/woz-case.viewmodel';

/**
 * Bridges the shared `ChatbotStorageAdapter` interface to the unified
 * `woz-active-case` localStorage key.
 *
 * Instead of writing to a separate `woz-case-chat` key, messages are
 * read from / written to `WozCaseViewModel.state.data.messages`.
 * Actual persistence to localStorage happens via the case viewmodel's
 * normal `persist()` cycle.
 *
 * IMPORTANT: The viewmodel must be passed in the constructor so that
 * `load()` works synchronously when `ChatbotViewModel` calls `restore()`
 * during its own construction.
 */
export class WozCaseChatbotStorageAdapter implements ChatbotStorageAdapter {
  private readonly caseViewModel: WozCaseViewModel;

  constructor(caseViewModel: WozCaseViewModel) {
    this.caseViewModel = caseViewModel;
  }

  save(snapshot: ChatbotSnapshot): void {
    // Write messages into the case data. The case viewmodel's persist()
    // cycle will pick them up and write to woz-active-case in localStorage.
    this.caseViewModel.updateMessages(snapshot.messages);
  }

  load(): ChatbotSnapshot | null {
    const data = this.caseViewModel.state.getValue().data;
    if (!data || data.messages.length === 0) return null;

    return { messages: data.messages };
  }

  clear(): void {
    this.caseViewModel.updateMessages([]);
  }
}
